import React from "react";
import { IonCard, IonCardHeader, IonCardContent, IonText, IonIcon } from "@ionic/react";
import classNames from "classnames/bind";

import { Product, ImageType, ProductImageTypes, ProductType } from "@core/types";

import styles from "./NewListViewProduct.module.scss";

import { useProductCardActions } from "../../../../../providers/useProductActions";
import { Image } from "../../../../../components/Image/Image";
import { useCartData } from "../../../../../providers/cartDataStore";
import { calculateDiscount } from "../../../../../utils/calculateDiscount";
import { useProductSubscriptionsStore } from "../../../../../providers/products";
import useAddProductSubscription from "../../../../../hooks/useAddProductSubscription";
import { useWishlistToggle } from "../../../../../providers/wishlist";
import { heart } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import useOpenProductDetailSheet from "../../../../../hooks/useOpenProductDetailSheet";
import { QuantityInput } from "../QuantityInput";
import { useConfig } from "../../../../../providers/config";
import { getProductQuantityStep } from "../../../../../utils/productQuantity";
import { prettifyNumbers } from "../../../../../utils/prettifyNumbers";

interface ProductCartProps {
  className?: string;
  product: Product;
  lazyload?: boolean;
}

const cx = classNames.bind(styles);

const NewListViewProduct = ({ product, lazyload, className }: ProductCartProps) => {
  const { t } = useTranslation();
  const config = useConfig();
  const { addToCart, removeProductFromCart } = useProductCardActions();
  const openProductDetailSheet = useOpenProductDetailSheet();

  const { toggleWish, isWishProduct } = useWishlistToggle();

  const handleOpenProductDetail = () => {
    openProductDetailSheet({ id: product.id, productType: product.type });
  };

  const handleAddToCart = () => {
    if (product.type === ProductType.VARIABLE) {
      handleOpenProductDetail();
      return;
    }

    addToCart(product);
  };

  const { productSubscriptionIdsMap } = useProductSubscriptionsStore.getState();
  const productSubscription = productSubscriptionIdsMap[product.id];
  const { handleAddProductSubscription, isLoading } = useAddProductSubscription(product.id);

  const productImage = { ...product?.images[0], type: ProductImageTypes.img } as ImageType;

  const discount = calculateDiscount(
    product.type,
    product.onSale,
    product.price,
    product.regularPrice,
    product.priceHtml,
  );
  const { cartProductsMap } = useCartData();

  const cartQuantity = cartProductsMap[product.id]?.quantity ?? 0;
  const isDisabled =
    (product.stockQuantity > cartQuantity && product.stockQuantity - cartQuantity < 1) ||
    product.stockQuantity === cartQuantity;

  const roundedCartQuantity = cartQuantity.toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    compactDisplay: "short",
    maximumFractionDigits: 2,
  });

  const isDiscount =
    product.discountPrice &&
    product.discountPrice !== "0" &&
    product.discountPrice !== "0.00" &&
    product.discountPrice !== product.price;

  const roundedStockQuantity = product.stockQuantity.toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    compactDisplay: "short",
    maximumFractionDigits: 2,
  });

  const roundedWeight = Number(product.weight).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    compactDisplay: "short",
    maximumFractionDigits: 2,
  });

  const portionUnit = product.portionUnit?.length ? product.portionUnit : "PCE";

  return (
    <div className="flex h-full flex-col items-center gap-1" onClick={handleOpenProductDetail}>
      <IonCard className={cx("ion-no-margin", "parhato2-product-card", "h-full w-full bg-card", className)}>
        <IonCardHeader className={cx("ion-no-padding cursor-pointer", "product-header", "relative")}>
          <div
            className="flex items-center absolute top-0 right-0 bg-muted rounded-full p-1.5"
            onClick={(e) => {
              e.stopPropagation();
              toggleWish(product?.id!);
            }}
          >
            <IonIcon
              className={`text-[1rem] ${isWishProduct(product?.id!) ? "text-red-500" : "text-gray-300"}`}
              icon={heart}
            />
          </div>

          <Image
            image={productImage}
            placeholder={productImage.base64}
            className={cx("product-image", "rounded-2xl")}
            lazyLoad={false}
          />
          {!!discount && <div className={cx("discount-badge")}>-{discount?.toFixed(0)}%</div>}
        </IonCardHeader>

        <IonCardContent className={cx("ion-no-padding", "product-content")}>
          <div className={cx("price-container")}>
            {isDiscount && (
              <IonText color="medium" className={cx("whitespace-nowrap text-xs font-bold text-[#FF0000]")}>
                {product.discountPrice}₽/{t(portionUnit)}
              </IonText>
            )}
            <IonText
              className={cx(
                "whitespace-nowrap text-sm font-bold text-foreground",
                isDiscount && "line-through text-xs text-muted-foreground",
              )}
            >
              {product.price} ₽/{t(portionUnit)}
            </IonText>
          </div>

          <IonText className={cx("product-name")}>{product.name}</IonText>

          <div className={cx("product-meta")}>
            {product.stockQuantity > 0 ? (
              <>
                <div
                  className={cx("weight", {
                    invisible: product.weight === "0",
                  })}
                >
                  {roundedWeight}
                  {t("KGM")}
                </div>
                <div className={cx("count")}>
                  {roundedStockQuantity}
                  {t(portionUnit)}
                </div>
                <div className={cx("current-price", isDiscount && "text-[#FF0000]")}>
                  {isDiscount ? product.discountPrice : product.price}₽/{t(portionUnit)}
                </div>
              </>
            ) : (
              <>
                <IonText color="danger" className={cx("outOfStock")}>
                  {t("Out of stock")}
                </IonText>
                <IonText className={cx("current-price", isDiscount && "text-[#FF0000]")}>
                  {isDiscount ? product.discountPrice : product.price}₽/{t(portionUnit)}
                </IonText>
              </>
            )}
          </div>

          {product.stockQuantity > 0 ? (
            cartQuantity > 0 ? (
              <div
                className="flex items-center justify-between text-xs h-[2.125rem] border border-primary rounded-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeProductFromCart(product.id);
                  }}
                  className="flex-1 text-primary"
                >
                  <p className="font-bold text-center">-</p>
                </button>
                {config.isCartQuantityInputEnabled ? (
                  <QuantityInput
                    product={product}
                    cartQuantity={cartQuantity}
                    className="w-9 h-6 text-center bg-gray-100 rounded-md outline-none mx-0.5"
                  />
                ) : (
                  <span
                    onClick={(e) => e.stopPropagation()}
                    className="font-bold mx-2 px-1.5 py-0.5 bg-gray-100 rounded-sm"
                  >
                    {prettifyNumbers(cartQuantity)}
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart();
                  }}
                  disabled={isDisabled}
                  className={`flex-1 ${isDisabled ? "text-muted-foreground" : "text-primary"}`}
                >
                  <p className="font-bold text-center">+</p>
                </button>
              </div>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart();
                }}
                className="h-[2.125rem] w-full text-xs rounded-2xl m-0 p-0 bg-primary text-black"
              >
                {t("Add to cart")}
              </button>
            )
          ) : (
            <button
              disabled={!!productSubscription || isLoading}
              onClick={(e) => {
                e.stopPropagation();
                handleAddProductSubscription();
              }}
              className={`w-full text-xs rounded-lg py-2.5 font-semibold text-white bg-[#CDCDCD] ${
                !!productSubscription || isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {productSubscription ? t("Coming soon") : t("Notify")}
            </button>
          )}
        </IonCardContent>
      </IonCard>
    </div>
  );
};

NewListViewProduct.displayName = "PARHATO2_NewListViewProduct";

export default NewListViewProduct;
